<template>
  <v-card variant="outlined">
    <v-card-title class="d-flex flex-wrap align-center justify-space-between ga-3">
      <div>
        <div class="text-subtitle-1 font-weight-bold">
          {{ $t("ncrm.integrations.externalApi.deliveries.title") }}
        </div>
        <div class="text-body-2 text-medium-emphasis">
          {{ $t("ncrm.integrations.externalApi.deliveries.subtitle") }}
        </div>
      </div>
      <v-btn variant="text" color="primary" :loading="loading" @click="$emit('refresh')">
        <v-icon start>mdi-refresh</v-icon>
        {{ $t("ncrm.integrations.externalApi.actions.refresh") }}
      </v-btn>
    </v-card-title>
    <v-divider />
    <v-card-text>
      <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4" />

      <div class="d-flex flex-column ga-3 mb-4">
        <v-text-field
          v-model="search"
          :label="$t('ncrm.integrations.externalApi.deliveries.filters.search')"
          hide-details="auto"
        />
        <v-select
          v-model="statusFilter"
          :items="statusFilterItems"
          item-title="label"
          item-value="value"
          :label="$t('ncrm.integrations.externalApi.deliveries.filters.status')"
          hide-details="auto"
        />
      </div>

      <v-row>
        <v-col cols="12" lg="5">
          <v-list v-if="filteredDeliveries.length" lines="three" class="pa-0">
            <v-list-item
              v-for="delivery in filteredDeliveries"
              :key="delivery.id"
              rounded="lg"
              class="mb-2"
              :active="delivery.id === selectedDeliveryId"
              @click="$emit('select-delivery', delivery.id)"
            >
              <v-list-item-title class="font-weight-medium">
                {{ delivery.external_id }}
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ delivery.endpoint_name || delivery.endpoint_key }}
              </v-list-item-subtitle>
              <div class="d-flex flex-wrap ga-2 mt-3">
                <v-chip size="small" :color="statusMeta(delivery.status).color" :variant="statusMeta(delivery.status).variant">
                  {{ delivery.status }}
                </v-chip>
                <v-chip color="info">
                  {{ delivery.source_system || "-" }}
                </v-chip>
                <v-chip
                  v-if="delivery.error_code"
                  color="error"
                >
                  {{ delivery.error_code }}
                </v-chip>
              </div>
            </v-list-item>
          </v-list>
          <div v-else class="py-8 text-center text-body-2 text-medium-emphasis">
            {{
              deliveries.length
                ? $t("ncrm.integrations.externalApi.deliveries.emptyFiltered")
                : $t("ncrm.integrations.externalApi.deliveries.empty")
            }}
          </div>
        </v-col>

        <v-col cols="12" lg="7">
          <NcrmExternalApiDeliveryDetailCard
            v-if="visibleSelectedDelivery"
            :delivery="visibleSelectedDelivery"
          />
          <v-alert v-else type="info">
            {{ $t("ncrm.integrations.externalApi.deliveries.selectHint") }}
          </v-alert>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import {
  filterExternalApiDeliveries,
  resolveExternalApiStatusMeta,
} from "@/Utile/ncrmExternalApi.mjs";
import NcrmExternalApiDeliveryDetailCard from "@/components/ncrm/integrations/NcrmExternalApiDeliveryDetailCard.vue";

export default {
  name: "NcrmExternalApiDeliveriesCard",
  components: {
    NcrmExternalApiDeliveryDetailCard,
  },
  props: {
    deliveries: {
      type: Array,
      default: () => [],
    },
    selectedDelivery: {
      type: Object,
      default: null,
    },
    selectedDeliveryId: {
      type: Number,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    pinnedDeliveryId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      search: "",
      statusFilter: "all",
    };
  },
  computed: {
    filteredDeliveries() {
      return filterExternalApiDeliveries(this.deliveries, {
        search: this.search,
        status: this.statusFilter,
      });
    },
    statusFilterItems() {
      return [
        { label: this.$t("ncrm.integrations.externalApi.deliveries.filters.all"), value: "all" },
        {
          label: this.$t("ncrm.integrations.externalApi.deliveries.filters.options.stored"),
          value: "stored",
        },
        {
          label: this.$t("ncrm.integrations.externalApi.deliveries.filters.options.rejected"),
          value: "rejected",
        },
      ];
    },
    visibleSelectedDelivery() {
      if (!this.selectedDelivery?.id) return null;
      // Always show if the delivery was explicitly deeplinked (e.g. navigated from workflow table)
      if (this.pinnedDeliveryId && this.selectedDelivery.id === this.pinnedDeliveryId) {
        return this.selectedDelivery;
      }
      return this.filteredDeliveries.some((delivery) => delivery.id === this.selectedDelivery.id)
        ? this.selectedDelivery
        : null;
    },
  },
  methods: {
    statusMeta(status) {
      return resolveExternalApiStatusMeta(status);
    },
  },
};
</script>
